import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPlatformMethodology } from 'api/platformMethodologies';
import { getPlatformPractices } from 'api/platformPractices';
import { Flex, Line, Spinner } from 'components';
import { BackButton } from 'components/BackButton';
import { Button } from 'components/_form';
import { H1 } from 'components/Typography/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';
import { IPlatformMethodology } from 'types/platformResources';
import { IPlatformPractice } from 'types/platformPractice';
import { Wrapper } from 'views/AdministrationView/views/CompanyAdministrationView/CompanyAdministrationView.styled';
import { AddPlatformPracticeForm } from '../PlatformPracticesView/components/AddPlatformPracticeForm/AddPlatformPracticeForm';
import { EditPlatformPracticeForm } from '../PlatformPracticesView/components/EditPlatformPracticeForm/EditPlatformPracticeForm';
import { PlatformPracticesTable } from '../PlatformPracticesView/components/PlatformPracticesTable/PlatformPracticesTable';

export const PlatformMethodologyDetailView = () => {
  const { methodologyId } = useParams<{ methodologyId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = Number(methodologyId);

  const [methodology, setMethodology] = useState<IPlatformMethodology>();
  const [practices, setPractices] = useState<IPlatformPractice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingPracticeId, setEditingPracticeId] = useState<number>();

  const loadData = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const [methodologyRes, practicesRes] = await Promise.all([
        getPlatformMethodology(id),
        getPlatformPractices([id])
      ]);

      setMethodology(methodologyRes.data);
      setPractices(practicesRes.data);
    } catch (error) {
      showPlatformApiError(error, t);
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Wrapper mb={94} flexDirection="column">
      <Flex width="100%" alignItems="center">
        <BackButton />
        <H1 m={2} variant={['h3', 'h2']} textTransform="uppercase">
          {t('platform.title')} | {methodology?.name || '…'}
        </H1>
        <Flex ml="auto">
          <Button variant="eucalyptus" minWidth="200px" onClick={() => setIsAddOpen(true)}>
            <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '8px' }} />
            {t('platformPractices.addPractice')}
          </Button>
        </Flex>
      </Flex>
      <Line />

      {isLoading ? (
        <Spinner />
      ) : (
        <PlatformPracticesTable
          practices={practices}
          hideMethodology
          onEdit={(practice) => setEditingPracticeId(practice.id)}
          onOpen={(practice) =>
            navigate(`/app/platform/resources/practices/${practice.id}`)
          }
        />
      )}

      {methodology && (
        <AddPlatformPracticeForm
          isOpen={isAddOpen}
          defaultMethodology={methodology}
          onCancelClick={() => setIsAddOpen(false)}
          onSuccess={() => {
            setIsAddOpen(false);
            loadData();
          }}
        />
      )}

      {editingPracticeId && (
        <EditPlatformPracticeForm
          isOpen={!!editingPracticeId}
          editingPracticeId={editingPracticeId}
          onCancelClick={() => setEditingPracticeId(undefined)}
          onSubmitSuccess={() => {
            setEditingPracticeId(undefined);
            loadData();
          }}
          onDeleteSuccess={() => {
            setEditingPracticeId(undefined);
            loadData();
          }}
        />
      )}
    </Wrapper>
  );
};
